import { toJS } from '@rehaje/reactive'
import {
  getIterableKeys,
  getJSValidKey,
  getPrototypeOf,
  isNotEmpty,
  isPlainObj,
  objectPrototype
} from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaIdempotenceError,
  SchemaMaxPropertiesError,
  SchemaMinPropertiesError,
  SchemaTransformError
} from '../../errors'
import { createJSDoc } from '../../lambdas/createJSDoc'
import { SchemaDefinition } from '../../models/SchemaDefinition'
import { TypedNode } from '../../models/TypedNode'

import type {
  RuntimeSchemaSpecific,
  SchemaObjectSpecific,
  SnapshotSpecificLike,
  TypedSchema
} from '../../types'
import type { TRecord } from '@rehaje/shared'

export class SchemaObjectDefinition extends SchemaDefinition {
  static __r_idempotence = false

  __r_eachProperties(
    target: any,
    schema: SchemaObjectSpecific,
    callback: (
      property: TypedSchema | undefined,
      name: string
    ) => boolean | void
  ) {
    const properties = schema.properties ?? []
    const keys = getIterableKeys(target)
    const props: Record<PropertyKey, TypedSchema> = {}
    properties.forEach((property, index) => {
      let name = property.name
      if (!name) {
        if (property.type !== 'Schema.Void') return
        name = '__r_void' + index
      }
      props[name] = property
      if (!keys.includes(name)) keys.push(name)
    })
    for (const key of keys) {
      if (callback(props[key] ?? schema.additionalProperties, key) === false)
        return
    }
  }

  __r_eachTypeScriptProperties(
    target: any,
    schema: SchemaObjectSpecific,
    callback: (
      property: TypedSchema | undefined,
      name?: string
    ) => boolean | void
  ) {
    if (target === undefined) {
      for (const property of schema.properties ?? []) {
        if (callback(property, property.name) === false) return
      }
    } else {
      const propsKeys: string[] = getIterableKeys(target)
      const propsMaps: Record<string, TypedSchema> = {}
      for (const property of schema.properties ?? []) {
        if (property.name && propsKeys.includes(property.name)) {
          propsMaps[property.name] = property
        }
      }
      for (const prop of propsKeys) {
        const propSchema = propsMaps[prop] ?? schema.additionalProperties
        if (callback(propSchema, prop) === false) return
      }
    }
  }

  __r_mapProperties<T>(
    target: any,
    schema: SchemaObjectSpecific,
    callback: (property: TypedSchema | undefined, name: string) => T
  ): T[] {
    const result: T[] = []
    this.__r_eachProperties(target, schema, (property, name) => {
      if (name === undefined) return
      result.push(callback(property, name))
    })
    return result
  }

  override defaultsMerge(
    value: TRecord | undefined,
    schema: SchemaObjectSpecific,
    initialize: boolean
  ) {
    if (value === undefined && (initialize || schema.required))
      value = schema.default
    if (value === undefined || isPlainObj(value)) {
      if (TypedNode.__r_deep_defaults_merge && isPlainObj(schema.default)) {
        value = Object.assign({}, schema.default, value)
      }
      schema.properties?.forEach((property) => {
        const name = property.name
        if (name === undefined) return
        const defaults = super.defaultsMerge(
          value?.[name],
          property,
          initialize
        )
        if (defaults === undefined) return
        value ??= {}
        value[name] = defaults
      })
    }
    return value
  }

  override getIn(
    schema: SchemaObjectSpecific,
    path: PropertyKey[]
  ): TypedSchema {
    if (path.length === 0) return schema
    const properties = schema.properties ?? []
    const additionalProperties = schema.additionalProperties
    const result = properties.reduce(
      (predicate: TypedSchema | undefined, property) => {
        if (predicate) return predicate
        if (path[0] === property.name) {
          return super.getIn(property, path.slice(1))
        }
        return predicate
      },
      undefined
    )
    if (result) return result
    if (additionalProperties)
      return super.getIn(additionalProperties, path.slice(1))
    return UnknownSchema
  }

  override infer(
    target: any,
    schema?: SchemaObjectSpecific,
    path: PropertyKey[] = []
  ): SchemaObjectSpecific {
    if (typeof schema?.infer === 'function') {
      return schema.infer.call(
        this,
        target,
        (arg0 = target, arg1 = schema, arg2 = path) => {
          return this.infer(arg0, { ...arg1, infer: undefined }, arg2)
        }
      )
    }

    const result: SchemaObjectSpecific = {
      ...schema,
      type: 'Schema.Object',
      get value() {
        return toJS(target)
      }
    }
    if (schema && (target === undefined || isPlainObj(target))) {
      const properties: TypedSchema[] = []
      this.__r_eachProperties(target, schema, (property, name) => {
        let inferred
        if (name === undefined) {
          inferred = super.infer(undefined, property, path, target)
        } else {
          inferred = super.infer(
            target?.[name],
            property,
            path.concat(name),
            target
          )
          inferred = { ...inferred, name }
        }
        properties.push(inferred)
      })
      result.properties = properties
    }
    return result
  }

  override inferOnlySchema(
    schema: SchemaObjectSpecific,
    path: PropertyKey[] = []
  ): SchemaObjectSpecific {
    return {
      ...schema,
      additionalProperties: schema.additionalProperties
        ? super.inferOnlySchema(schema.additionalProperties, path.concat('*'))
        : undefined,
      properties: schema.properties?.map((property) =>
        super.inferOnlySchema(property, path.concat(property.name!))
      )
    }
  }

  override inferTypeScript(
    schema: SchemaObjectSpecific,
    path: PropertyKey[]
  ): string {
    const { additionalProperties, value } = schema
    const pairs: string[][] = []
    this.__r_eachTypeScriptProperties(value, schema, (property, name) => {
      if (!property) {
        if (name !== undefined) pairs.push([getJSValidKey(name), 'unknown'])
      } else if (property?.type === 'Schema.Void') {
        for (const type of property.types ?? []) {
          if (type.type === 'Schema.Void' || type.name === undefined) continue
          pairs.push([
            `${createJSDoc(type)}${getJSValidKey(type.name)}`,
            super.inferTypeScript(type, path.concat(type.name)),
            type.required ? ':' : '?:'
          ])
        }
      } else if (name !== undefined) {
        pairs.push([
          `${createJSDoc(property)}${getJSValidKey(name)}`,
          super.inferTypeScript(property, path.concat(name)),
          property.required ? ':' : '?:'
        ])
      }
    })
    if (additionalProperties) {
      pairs.push([
        `${createJSDoc(schema)}[key:string]`,
        super.inferTypeScript(additionalProperties, path.concat('*'))
      ])
    }
    return `{${pairs.map(([key, value, separator = ':']) => `${key}${separator}${value};`).join('')}}`
  }

  override prune(
    target: SnapshotSpecificLike,
    schema: SchemaObjectSpecific,
    path?: PropertyKey[]
  ) {
    if (!isPlainObj(target)) return target

    if (schema.additionalProperties) return target

    if (!schema.properties || schema.properties.length === 0) {
      return target
    }

    const result: TRecord = {}
    for (const property of schema.properties ?? []) {
      if (property.name && target[property.name] !== undefined) {
        result[property.name] = super.prune(
          target[property.name],
          property,
          path?.concat(property.name),
          result
        )
      }
    }

    // 剪枝的时候需要保留快照私有属性
    if (target.__r_snapshot_id) {
      result.__r_snapshot_id = target.__r_snapshot_id
      result.__r_order = target.__r_order
    }

    return result
  }

  override transform(
    target: any,
    schema: SchemaObjectSpecific,
    path: PropertyKey[] = []
  ) {
    if (target === null && schema.nullable) return target
    if (!isPlainObj(target)) {
      if (target !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback === undefined ? {} : schema.fallback
    }
    if (getPrototypeOf(target) !== objectPrototype) return target
    if (!schema.properties?.length && !schema.additionalProperties)
      return target
    const object = { ...target }
    this.__r_eachProperties(object, schema, (property, name) => {
      if (name === undefined) return
      else if (property?.type === 'Schema.Void') {
        super.transform(object, property, path, object)
      } else if (property?.type && object[name] !== undefined) {
        object[name] = super.transform(
          object[name],
          property,
          path.concat(name),
          object
        )
      }
    })
    return object
  }

  override validate(
    target: any,
    schema: any,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaObjectSpecific>,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ): void {
    if (this.validateMetadata(target, schema, isPlainObj, isNotEmpty)) return
    const targetKeys = getIterableKeys(target)
    if (schema.minProperties && targetKeys.length < schema.minProperties) {
      this.raise(new SchemaMinPropertiesError(schema.name, targetKeys.length))
    }
    if (schema.maxProperties && targetKeys.length > schema.maxProperties) {
      this.raise(new SchemaMaxPropertiesError(schema.name, targetKeys.length))
    }
    if (!recursion) return
    const validateProperties = () => {
      let idempotence = true
      const propertyKeys: string[] = []
      if (schema.properties) {
        for (const property of schema.properties) {
          const name = property.name
          if (!name || propertyKeys.includes(name)) continue
          if (!targetKeys.includes(name)) idempotence = false
          propertyKeys.push(name)
          super.validate(target[name], property, path.concat(name), target)
        }
      }
      if (schema.additionalProperties) {
        for (const key of targetKeys) {
          if (propertyKeys.includes(key)) continue
          idempotence = false
          super.validate(
            target[key],
            schema.additionalProperties,
            path.concat(key),
            target
          )
        }
      }
      if (targetKeys.length !== propertyKeys.length) idempotence = false
      if (!idempotence && SchemaObjectDefinition.__r_idempotence) {
        this.raise(new SchemaIdempotenceError(schema.name))
      }
    }
    if (schema.idempotence && !SchemaObjectDefinition.__r_idempotence) {
      SchemaObjectDefinition.__r_idempotence = true
      try {
        validateProperties()
      } finally {
        SchemaObjectDefinition.__r_idempotence = false
      }
    } else {
      validateProperties()
    }
  }
}
